<template>
  <WizardDialog
    :visible="visible"
    :phase="wizard.phase.value"
    :phases="PHASES"
    :titles="titles"
    :subtitles="subtitles"
    :locked="locked"
    :full-screen="true"
    :close-label="t('billing.migrate.close')"
    @update:visible="onVisibleChange"
    @close="onClose"
  >
    <!-- ─── Phase 1 : V5 plan picker (PlanComparator, V5-overridden) ───── -->
    <template #select>
      <div class="migrate-v5__phase-body">
        <!-- Error state: the V5 tiers grid could not be loaded (back error).
             Without this branch the wizard body would stay fully blank — the
             PlanComparator renders nothing when `tiers` is null. -->
        <div
          v-if="tiersErrorCode"
          class="migrate-v5__support"
        >
          <div class="migrate-v5__support-icon">
            <i class="fa-regular fa-circle-info" />
          </div>
          <h3 class="migrate-v5__support-title">
            {{ t('billing.migrate.support.title') }}
          </h3>
          <p class="migrate-v5__support-body">
            {{ t('billing.migrate.support.body') }}
          </p>
          <Button
            class="migrate-v5__support-cta p-button"
            icon="fa-regular fa-message"
            :label="t('billing.migrate.support.cta')"
            @click="openSupport"
          />
        </div>
        <PlanComparator
          v-else
          v-model="selectedPlan"
          v-model:frequency="selectedFrequency"
          v-model:targetTier="selectedTier"
          :current-plan-code="currentV4PlanCode"
          offer-override="v5"
          :migration-mode="true"
          :current-offer-banner="currentOfferBanner"
          @confirm="onPlanConfirmed"
          @error="onTiersError"
        />
      </div>
    </template>

    <!-- ─── Phase 2 : Review + payment ──────────────────────────────────── -->
    <template #review>
      <div class="migrate-v5__phase-body">
        <div
          v-if="loadingPreview"
          class="migrate-v5__loading"
        >
          <ProgressSpinner style="width: 36px; height: 36px" />
        </div>

        <Message
          v-else-if="errorMessage"
          severity="error"
          :closable="false"
        >
          {{ errorMessage }}
        </Message>

        <ReviewAndPayCard
          v-else-if="preview"
          v-model:payment-mode="paymentMode"
          v-model:payment-method-id="paymentMethodId"
          :recap-title="t('billing.migrate.targetTitle', { plan: preview.target.planName })"
          :recap-subtitle="recapSubtitle"
          recap-icon="fa-regular fa-rocket"
          :recap-lines="recapLines"
          :total-label="t('billing.migrate.prorataToday')"
          :total-value="(preview.totalToPay ?? preview.target.prorataAmount).label"
          :default-card="defaultCard"
          :all-cards="allCards"
          :amount="(preview.totalToPay ?? preview.target.prorataAmount).amount"
          :currency="currency"
          :allowed-methods="ALLOWED_METHODS_PLAN"
          :dropin-submit-handler="onDropinSubmit"
          :dropin-details-handler="onDropinDetails"
          :payment-error="paymentErrorMessage"
          :cta-label="ctaLabel"
          :cta-icon="ctaIcon"
          :cta-submitting="submitting"
          @dropin-success="onDropinSuccess"
          @dropin-error="onDropinError"
          @cta-click="onConfirm"
        >
          <template #recap-extra>
            <div
              v-if="preview.nextRenewalDate"
              class="migrate-v5__next"
            >
              <i class="fa-regular fa-calendar" />
              <span>{{ t('billing.migrate.nextRenewal', { date: formatDate(preview.nextRenewalDate) }) }}</span>
            </div>
          </template>

          <template
            v-if="isShopify"
            #payment-content
          >
            <div class="migrate-v5__shopify">
              <div class="migrate-v5__shopify-icon">
                <i class="fa-brands fa-shopify" />
              </div>
              <p class="migrate-v5__shopify-text">
                {{ t('billing.payment.shopify.notice') }}
              </p>
            </div>
          </template>
        </ReviewAndPayCard>
      </div>

      <div
        v-if="!locked"
        class="migrate-v5__foot"
      >
        <WizardBackButton
          :label="t('billing.migrate.back')"
          @click="wizard.back"
        />
      </div>
    </template>
  </WizardDialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import Button from 'primevue/button';
import Gleap from 'gleap';
import moment from 'moment';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import WizardBackButton from '@/components/billing/shared/WizardBackButton.vue';
import PlanComparator from '@/components/billing/dialogs/PlanComparator.vue';
import ReviewAndPayCard, { ReviewAndPayLine } from '@/components/billing/shared/ReviewAndPayCard.vue';
import {
  ActionResult,
  ALLOWED_METHODS_PLAN,
  confirmPlan,
  previewV5Migration,
  subscribePlan,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { useWizard } from '@/composables/billing/useWizard';
import { PaymentMethodItem } from '@/types/billing-types';

const PHASES = ['select', 'review'] as const;

type PaymentMode = 'existing' | 'new';

/**
 * V3/V4 → V5 migration dialog. Reuses {@link PlanComparator} for the plan
 * picker phase (with `offerOverride='v5'` + `migrationMode=true`) so the
 * V5 catalog is presented exactly like the regular plan-change flow —
 * tier selector, all V5 plans side by side, recommendation ribbon. The
 * user's current offer sits in a banner above the grid, never conflicting
 * with the V5 grid's own "current" semantics: built from `overview.plan`
 * for V4 shops, from `overview.legacyPlan` (bundle name, no comparable
 * monthly price) for V3 shops.
 *
 * Phase 2 stays on {@link ReviewAndPayCard} : single recap-and-pay card
 * driven by {@link previewV5Migration} for the chosen plan + frequency,
 * confirmed with `subscribePlan({ migrateToV5: true })`.
 */
export default defineComponent({
  name: 'MigrateV5Dialog',

  components: {
    WizardDialog,
    WizardBackButton,
    PlanComparator,
    ReviewAndPayCard,
    ProgressSpinner,
    Message,
    Button,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'migrated'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();
    const wizard = useWizard(PHASES);

    // ─── Selection state ───────────────────────────────────────────────
    const selectedPlan = ref<string | null>(null);
    const selectedFrequency = ref<'monthly' | 'yearly'>('monthly');
    const selectedTier = ref<number | undefined>(undefined);

    // Phase 1 error — set when the PlanComparator fails to load the V5
    // tiers grid. Replaces the comparator with a "contact support" block
    // instead of leaving the full-screen wizard blank.
    const tiersErrorCode = ref<string | null>(null);
    const onTiersError = (code: string) => {
      tiersErrorCode.value = code;
    };

    const openSupport = () => {
      try {
        Gleap.open();
      } catch {
        // Gleap not initialised — fallback to a direct mailto.
        window.open(
          'mailto:contact@shopimind.com?subject=Demande%20de%20support%20-%20Migration%20V5',
          '_blank',
        );
      }
    };

    // ─── Phase 2 state ─────────────────────────────────────────────────
    const preview = ref<any | null>(null);
    const loadingPreview = ref(false);
    const errorMessage = ref<string | null>(null);
    const paymentMode = ref<PaymentMode>('existing');
    const paymentMethodId = ref<string | null>(null);
    const paymentErrorMessage = ref<string | null>(null);
    const submitting = ref(false);
    const locked = ref(false);

    // ─── Overview-derived state ────────────────────────────────────────
    const overview = computed(() => store.state.billing.overview);
    const isShopify = computed(() => overview.value?.shop?.solutionEcommerce === 'shopify');
    const currency = computed<'EUR' | 'USD'>(
      () => (overview.value?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    const allCards = computed<PaymentMethodItem[]>(
      () => overview.value?.paymentMethods?.items ?? [],
    );
    const defaultCard = computed(
      () => allCards.value.find((p: any) => p.isDefault) ?? null,
    );
    const hasDefaultCard = computed(() => defaultCard.value !== null);
    const currentV4PlanCode = computed<string | null>(
      () => overview.value?.plan?.code ?? null,
    );

    // V3-offer shop (legacy bundles) — the modern `overview.plan` is null,
    // its subscription lives in `overview.legacyPlan`.
    const isLegacyOffer = computed(
      () => overview.value?.capabilities?.isLegacy === true,
    );

    // Banner content driving PlanComparator's "current offer" callout —
    // built from the same overview the rest of the page reads, so the
    // plan name + monthly price are always in sync with the user's actual
    // subscription. V3 shops surface their legacy bundle name instead (no
    // comparable monthly price on the bundles model).
    const currentOfferBanner = computed(() => {
      if (isLegacyOffer.value) {
        const legacy = overview.value?.legacyPlan;
        if (!legacy || legacy.status !== 'active') return null;
        return {
          planName: legacy.bundleName
            ? `${t('billing.migrate.v3Label')} — ${legacy.bundleName}`
            : t('billing.migrate.v3Label'),
          subtitle: legacy.metricTotal
            ? t('billing.migrate.basedOnPv', {
              value: new Intl.NumberFormat('fr-FR').format(legacy.metricTotal.value),
            })
            : undefined,
        };
      }
      const plan = overview.value?.plan;
      if (!plan || !plan.name) return null;
      const priceLabel = plan.pricePaid?.label ?? plan.price?.label ?? '—';
      const metric = plan.currentMetric;
      return {
        planName: `${t('billing.migrate.v4Label')} — ${plan.name}`,
        priceLabel,
        subtitle: metric
          ? t('billing.migrate.basedOnPv', {
            value: new Intl.NumberFormat('fr-FR').format(metric.value),
          })
          : undefined,
      };
    });

    // ─── Titles / subtitles per phase ──────────────────────────────────
    const titles = computed(() => ({
      select: t('billing.migrate.titleSelect'),
      review: t('billing.migrate.titleReview'),
    }));
    const subtitles = computed(() => ({
      // V3 shops have no "offre V4 actuelle" to compare against — use the
      // version-neutral wording.
      select: isLegacyOffer.value
        ? t('billing.migrate.subtitleSelectV3')
        : t('billing.migrate.subtitleSelect'),
      review: t('billing.migrate.subtitleReview'),
    }));

    // ─── Review phase computed ─────────────────────────────────────────
    const recapSubtitle = computed(() => (
      selectedFrequency.value === 'yearly'
        ? t('billing.compare.yearly')
        : t('billing.compare.monthly')
    ));

    const recapLines = computed<ReviewAndPayLine[]>(() => {
      if (!preview.value) return [];
      // Fallback label when the current offer has no plan name — the back
      // now tags the origin offer ('v3' | 'v4') on the preview.
      const currentOfferLabel = preview.value.current?.offerVersion === 'v3'
        ? t('billing.migrate.v3Label')
        : t('billing.migrate.v4Label');
      // Note: `monthlyPrice.label` already ends with " / mois" — do NOT
      // append `monthlyShort` again or the value renders "X € / mois / mois".
      const lines: ReviewAndPayLine[] = [
        {
          label: t('billing.migrate.currentPlanLine'),
          value: preview.value.current.monthlyPrice
            ? `${preview.value.current.planName ?? currentOfferLabel} · ${preview.value.current.monthlyPrice.label}`
            : (preview.value.current.planName ?? currentOfferLabel),
        },
        {
          label: t('billing.migrate.newPlanLine'),
          value: `${preview.value.target.planName} · ${preview.value.target.monthlyPrice.label}`,
          kind: 'accent',
        },
      ];
      // Prorata + credit lines explain how `totalToPay` was derived. The
      // monthly delta line (delta.monthlyDifference) was removed — its info
      // is already conveyed by the current/new plan lines above plus the
      // total, and the redundant "Variation mensuelle" was noise.
      const days = preview.value.breakdown?.daysRemaining;
      const total = preview.value.breakdown?.daysInCurrentCycle;
      if (
        preview.value.target?.prorataAmount
        && preview.value.target.prorataAmount.amount !== preview.value.target.baseAmount?.amount
      ) {
        lines.push({
          label: days != null && days > 0
            ? t('billing.migrate.prorataNewWithDays', { days })
            : t('billing.migrate.prorataNew'),
          value: preview.value.target.prorataAmount.label,
          kind: 'accent',
        });
      }
      if (
        preview.value.creditFromCurrent
        && preview.value.creditFromCurrent.amount > 0
      ) {
        lines.push({
          label: days != null && days > 0
            ? t('billing.migrate.creditCurrentWithDays', { days })
            : t('billing.migrate.creditCurrent'),
          value: `− ${preview.value.creditFromCurrent.label}`,
          kind: 'discount',
        });
      }
      return lines;
    });

    const ctaLabel = computed(() => {
      if (isShopify.value) return t('billing.payment.shopify.cta');
      return hasDefaultCard.value ? t('billing.migrate.confirm') : '';
    });
    const ctaIcon = computed(() => (
      isShopify.value ? 'fa-brands fa-shopify' : 'fa-regular fa-rocket'
    ));

    // ─── API calls ─────────────────────────────────────────────────────
    const loadPreview = async () => {
      if (!selectedPlan.value) return;
      loadingPreview.value = true;
      errorMessage.value = null;
      preview.value = null;
      try {
        const result = await previewV5Migration({
          plan: selectedPlan.value,
          frequency: selectedFrequency.value,
        });
        if (!result) {
          errorMessage.value = t('billing.migrate.cannotPreview');
        } else {
          preview.value = result;
        }
      } finally {
        loadingPreview.value = false;
      }
    };

    // Triggered when the user clicks "Migrer vers {plan}" on a card in the
    // PlanComparator. The plan + frequency are already in v-model; load the
    // preview and move to phase 2.
    const onPlanConfirmed = async (code: string) => {
      selectedPlan.value = code;
      wizard.next();
      await loadPreview();
    };

    const onConfirm = async () => {
      if (!selectedPlan.value) return;
      submitting.value = true;
      paymentErrorMessage.value = null;
      try {
        const result = await subscribePlan({
          plan: selectedPlan.value,
          frequency: selectedFrequency.value,
          migrateToV5: true,
          ...(selectedTier.value !== undefined
            ? { targetTier: selectedTier.value }
            : {}),
          ...(paymentMethodId.value
            ? { paymentMethodId: paymentMethodId.value }
            : {}),
        });
        if (result.status === 'completed') {
          // Comme la nouvelle carte : le plan V5 est posé par le webhook Adyen
          // asynchrone. On délègue à @migrated → onPlanSubscribed (poll +
          // reload) plutôt que le reload immédiat de handleReload, qui courrait
          // le webhook. Les autres statuts restent gérés par handleReload.
          locked.value = true;
          emit('migrated');
          emit('update:visible', false);
          setTimeout(reset, 220);
        } else {
          await handleReload(result, {
            onFailure: (err) => {
              paymentErrorMessage.value = err;
            },
            onShopifyApproval: (confirmationUrl) => {
              locked.value = true;
              window.location.href = confirmationUrl;
            },
            onActionRequired: () => {
              paymentErrorMessage.value = t('billing.payment.actionRequired');
            },
          });
        }
      } finally {
        submitting.value = false;
      }
    };

    // ─── Drop-in handlers (new-card path) ──────────────────────────────
    const onDropinSubmit = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => subscribePlan({
      plan: selectedPlan.value as string,
      frequency: selectedFrequency.value,
      migrateToV5: true,
      ...(selectedTier.value !== undefined
        ? { targetTier: selectedTier.value }
        : {}),
      adyenStateData,
    });

    const onDropinDetails = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => confirmPlan({ adyenStateData });

    const onDropinSuccess = () => {
      // Le plan V5 est appliqué par le webhook Adyen AUTHORISATION (asynchrone).
      // On délègue le rafraîchissement au parent (SubscriptionPage →
      // onPlanSubscribed) qui poll l'overview jusqu'à l'application du plan puis
      // recharge la page. Un refetch immédiat ici courrait le webhook et
      // laisserait l'ancien état jusqu'au prochain F5.
      emit('migrated');
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onDropinError = (err: string) => {
      paymentErrorMessage.value = err;
    };

    // ─── Lifecycle ─────────────────────────────────────────────────────
    const reset = () => {
      wizard.reset();
      selectedPlan.value = null;
      selectedFrequency.value = 'monthly';
      selectedTier.value = undefined;
      tiersErrorCode.value = null;
      preview.value = null;
      errorMessage.value = null;
      paymentErrorMessage.value = null;
      paymentMode.value = 'existing';
      paymentMethodId.value = null;
      locked.value = false;
    };

    const onVisibleChange = (v: boolean) => { if (!v) onClose(); };

    const onClose = () => {
      if (locked.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const formatDate = (iso: string) => moment(iso).format('DD/MM/YYYY');

    // Frequency change while on phase 2 → reload the preview to keep prices
    // in sync. Plan changes don't apply on phase 2 (user has to go back).
    watch(selectedFrequency, () => {
      if (wizard.phase.value === 'review') loadPreview();
    });

    // Switch to the right payment mode whenever the cards list changes.
    watch(hasDefaultCard, (has) => {
      paymentMode.value = has ? 'existing' : 'new';
    }, { immediate: true });

    watch(() => props.visible, (v) => { if (v) reset(); });

    return {
      t,
      ALLOWED_METHODS_PLAN,
      PHASES,
      wizard,
      titles,
      subtitles,
      selectedPlan,
      selectedFrequency,
      selectedTier,
      currentV4PlanCode,
      currentOfferBanner,
      tiersErrorCode,
      onTiersError,
      openSupport,
      preview,
      loadingPreview,
      errorMessage,
      paymentErrorMessage,
      submitting,
      locked,
      paymentMode,
      paymentMethodId,
      isShopify,
      currency,
      allCards,
      defaultCard,
      recapSubtitle,
      recapLines,
      ctaLabel,
      ctaIcon,
      onPlanConfirmed,
      onConfirm,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
      onVisibleChange,
      onClose,
      formatDate,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.migrate-v5 {
  &__phase-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 16px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__foot {
    display: flex;
    align-items: center;
    padding: 16px 0;
    background: #fff;
    flex-shrink: 0;
    border-top: 1px solid #f1f5f9;
  }

  &__loading {
    text-align: center;
    padding: 60px 0;
  }

  // "Contact support" state shown when the V5 tiers grid fails to load —
  // same visual language as ChangePlanDialog's support block.
  &__support {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 48px 24px;
    gap: 14px;
    max-width: 520px;
    margin: 0 auto;
  }

  &__support-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: #f1f5f9;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
  }

  &__support-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 4px 0 0;
  }

  &__support-body {
    font-size: 0.875rem;
    color: #475569;
    line-height: 1.55;
    margin: 0;
  }

  &__support-cta {
    margin-top: 8px;
  }

  &__next {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    font-size: 0.8125rem;
    color: #64748b;

    i { color: #94a3b8; }
  }

  &__shopify {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 18px;
    background: linear-gradient(135deg, #f7fbf2 0%, #ffffff 100%);
    border: 1px solid #ecf3e3;
    border-radius: 12px;
  }

  &__shopify-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #95bf47;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  &__shopify-text {
    margin: 0;
    font-size: 0.8125rem;
    color: #475569;
    line-height: 1.5;
  }
}
</style>
